import React, { useState, useEffect, useRef } from 'react';
import { api, fileUrl } from '../api';
import AdminExpenseEditModal from './AdminExpenseEditModal';
import AdminExpenseCreateModal from './AdminExpenseCreateModal';
import AuditTrail from './AuditTrail';
import { ALL_CATEGORY_VALUES } from '../utils/expenseCategories';
import './adminExpenses.css';

interface Expense {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  type: string;
  justification: string;
  amount: number;
  currency: string;
  reimbursement_amount: number;
  reimbursement_currency: string;
  exchange_rate: number;
  fetched_exchange_rate?: number;
  override_exchange_rate?: number;
  override_reason?: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  receipt: string | null;
  admin_notes: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  created_at: string;
  unique_id: string;
  expense_id?: string;
  classification?: string | null;
  vat_amount?: number | null;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  reimbursement_currency: string;
  active: boolean;
  created_at: string;
}

interface AdminExpensesTabProps {
  onMessage: (message: string, type: 'success' | 'error') => void;
}

const PAGE_SIZE = 100;
const FETCH_LIMIT = 5000;

const AdminExpensesTab = ({ onMessage }: AdminExpensesTabProps) => {
  const [expenses, setExpenses] = useState([] as Expense[]);
  const [users, setUsers] = useState([] as User[]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'submission' | 'expense'>('submission');
  const [selectedExpense, setSelectedExpense] = useState(null as Expense | null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingExpense, setRejectingExpense] = useState(null as Expense | null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [bulkMarkInProgress, setBulkMarkInProgress] = useState(false);
  // Duplicates state (admin-only feature)
  const [dupeLoading, setDupeLoading] = useState(false);
  const [dupeError, setDupeError] = useState<string>('');
  const [dupeList, setDupeList] = useState<Expense[]>([]);
  // Duplicate approval modal state
  const [showDupeApproveModal, setShowDupeApproveModal] = useState(false);
  const [approvingExpense, setApprovingExpense] = useState(null as Expense | null);
  const [approveDuplicates, setApproveDuplicates] = useState<Expense[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const token = localStorage.getItem('token');

  // Admin modals state
  const [showAdminEdit, setShowAdminEdit] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Pagination and summary state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [backendSummary, setBackendSummary] = useState<{
    submitted: number;
    approved: number;
    rejected: number;
    draft: number;
    paid: number;

    submitted_usd?: number;
    submitted_eur?: number;
    approved_usd?: number;
    approved_eur?: number;
    rejected_usd?: number;
    rejected_eur?: number;
    draft_usd?: number;
    draft_eur?: number;
    paid_usd?: number;
    paid_eur?: number;
  } | null>(null);
  const [backendTotalExpenses, setBackendTotalExpenses] = useState<number | null>(null);

  useEffect(() => {
    // Initial load, first page
    fetchData(0);
  }, []);

  // When user/status/category filters change, reload from backend starting at page 0.
  // We avoid doing this on the very first render using a ref guard.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setCurrentPage(0);
    fetchData(0);
  }, [userFilter, statusFilter, categoryFilter]);

  const fetchData = async (page: number) => {
    setLoading(true);
    try {
      // Always fetch a large single page so that sorting and search operate across
      // all matching rows for the current filters instead of just one paginated slice.
      const limit = FETCH_LIMIT;
      const offset = 0;

      // Build query params so backend filters server-side
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);

      if (userFilter) {
        const selectedUser = users.find(u => u.email === userFilter);
        if (selectedUser) {
          params.set('user_id', String(selectedUser.id));
        }
      }

      const [expensesResponse, usersResponse] = await Promise.all([
        api.get(`/api/admin/expenses-with-users?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        api.get('/api/admin/users', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      // Handle new paginated response format
      const expensesData = expensesResponse.data;
      let expensesList: any[];
      
      if (expensesData.expenses && expensesData.pagination) {
        // New paginated format
        expensesList = expensesData.expenses;
        setBackendSummary(expensesData.summary);
        setBackendTotalExpenses(
          typeof expensesData.pagination.total === 'number'
            ? expensesData.pagination.total
            : null
        );
        console.log('Pagination info:', expensesData.pagination);
        console.log('Summary stats:', expensesData.summary);
      } else {
        // Old format (backward compatibility)
        expensesList = expensesData || [];
        setBackendSummary(null);
        setBackendTotalExpenses(null);
      }
      
      // Normalize numeric fields for reliable calculations/rendering
      const normalized = expensesList.map((e: any) => ({
        ...e,
        amount: e.amount !== null && e.amount !== undefined ? Number(e.amount) : e.amount,
        reimbursement_amount: e.reimbursement_amount !== null && e.reimbursement_amount !== undefined ? Number(e.reimbursement_amount) : e.reimbursement_amount,
        exchange_rate: e.exchange_rate !== null && e.exchange_rate !== undefined ? Number(e.exchange_rate) : e.exchange_rate,
        fetched_exchange_rate: e.fetched_exchange_rate !== null && e.fetched_exchange_rate !== undefined ? Number(e.fetched_exchange_rate) : e.fetched_exchange_rate,
        override_exchange_rate: e.override_exchange_rate !== null && e.override_exchange_rate !== undefined ? Number(e.override_exchange_rate) : e.override_exchange_rate,
        vat_amount: e.vat_amount !== null && e.vat_amount !== undefined && e.vat_amount !== '' ? Number(e.vat_amount) : null
      }));
      
      setExpenses(normalized as Expense[]);
      setUsers(usersResponse.data || []);
      setTotalExpenses(normalized.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      onMessage('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseAction = async (expenseId: number, action: 'approve' | 'reject' | 'revert', adminNotes?: string, skipDuplicateCheck: boolean = false) => {
    try {
      // For approve action, check duplicates first unless explicitly skipped
      if (action === 'approve' && !skipDuplicateCheck) {
        const expense = expenses.find(e => e.id === expenseId);
        if (expense) {
          await handleApproveWithDuplicateCheck(expense);
          return;
        }
      }

      // Map 'revert' to 'unapprove' for the backend API (which reverts to submitted status)
      const apiAction = action === 'revert' ? 'unapprove' : action;
      
      // Prepare request body with admin notes for rejection
      const requestBody = adminNotes ? { admin_notes: adminNotes } : {};
      
      await api.put(`/api/admin/expenses/${expenseId}/${apiAction}`, requestBody, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Create user-friendly success messages
      const actionMessages = {
        approve: 'approved',
        reject: 'rejected', 
        revert: 'reverted to submitted'
      };
      
      onMessage(`Expense ${actionMessages[action]} successfully!`, 'success');
      // Refresh current page after status change
      fetchData(currentPage);
    } catch (error) {
      console.error(`Error ${action}ing expense:`, error);
      const actionMessages = {
        approve: 'approve',
        reject: 'reject',
        revert: 'revert'
      };
      onMessage(`Failed to ${actionMessages[action]} expense`, 'error');
    }
  };

  const handleApproveWithDuplicateCheck = async (expense: Expense) => {
    try {
      setCheckingDuplicates(true);
      setApprovingExpense(expense);
      
      // Fetch duplicates for this expense
      const expIdentifier = encodeURIComponent(getExpenseIdentifier(expense));
      const resp = await api.get(`/api/admin/expenses/${expIdentifier}/duplicates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const rows = (resp.data?.duplicates || []) as any[];
      const normalized = rows.map((e: any) => ({
        ...e,
        amount: e.amount !== null && e.amount !== undefined ? Number(e.amount) : e.amount,
        reimbursement_amount: e.reimbursement_amount !== null && e.reimbursement_amount !== undefined ? Number(e.reimbursement_amount) : e.reimbursement_amount,
        exchange_rate: e.exchange_rate !== null && e.exchange_rate !== undefined ? Number(e.exchange_rate) : e.exchange_rate,
        vat_amount: e.vat_amount !== null && e.vat_amount !== undefined && e.vat_amount !== '' ? Number(e.vat_amount) : null
      }));
      
      setApproveDuplicates(normalized as Expense[]);
      
      // If duplicates found, show warning modal
      if (normalized.length > 0) {
        setShowDupeApproveModal(true);
      } else {
        // No duplicates, proceed with approval
        await proceedWithApproval(expense.id);
      }
    } catch (error: any) {
      console.error('Failed to check duplicates:', error);
      onMessage('Failed to check for duplicates. Please try again.', 'error');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const proceedWithApproval = async (expenseId: number) => {
    try {
      await api.put(`/api/admin/expenses/${expenseId}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onMessage('Expense approved successfully!', 'success');
      // Refresh current page after approval
      fetchData(currentPage);
      setShowDupeApproveModal(false);
      setApprovingExpense(null);
      setApproveDuplicates([]);
    } catch (error) {
      console.error('Error approving expense:', error);
      onMessage('Failed to approve expense', 'error');
    }
  };

  const handleDupeApproveCancel = () => {
    setShowDupeApproveModal(false);
    setApprovingExpense(null);
    setApproveDuplicates([]);
  };

  const handleDupeApproveConfirm = async () => {
    if (approvingExpense) {
      await proceedWithApproval(approvingExpense.id);
    }
  };

  const handleRejectClick = (expense: Expense) => {
    setRejectingExpense(expense);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingExpense || !rejectionReason.trim()) {
      onMessage('Please provide a reason for rejection', 'error');
      return;
    }
    
    await handleExpenseAction(rejectingExpense.id, 'reject', rejectionReason);
    setShowRejectModal(false);
    setRejectingExpense(null);
    setRejectionReason('');
    closeSplitView();
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectingExpense(null);
    setRejectionReason('');
  };

  const handleBulkMarkAsPaid = async () => {
    if (bulkMarkInProgress) return;
    try {
      setBulkMarkInProgress(true);
      if (!userFilter) {
        onMessage('Select a user with approved expenses first', 'error');
        return;
      }

      const selectedUserObj = users.find(u => u.email === userFilter);
      if (!selectedUserObj) {
        onMessage('Unable to resolve user for bulk payment', 'error');
        return;
      }

      // Fetch ALL approved expenses for this user across pages
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('user_id', String(selectedUserObj.id));
      params.set('limit', '10000');
      params.set('offset', '0');

      const resp = await api.get(`/api/admin/expenses-with-users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = resp.data;
      const allRows: any[] = data.expenses && data.pagination ? data.expenses : (Array.isArray(data) ? data : []);
      const approvedForUser = allRows.filter((e: any) => e.status === 'approved');

      if (approvedForUser.length === 0) {
        onMessage('No approved expenses found for this user', 'error');
        return;
      }

      // Build expense_ids using the human-friendly expense_id
      const expenseIds = approvedForUser
        .map((e: any) => e.expense_id)
        .filter(Boolean) as string[];

      const user_name = `${selectedUserObj.first_name} ${selectedUserObj.last_name}`.trim() || (approvedForUser[0]?.user_name || '');

      if (!user_name) {
        onMessage('Unable to resolve user name for bulk payment', 'error');
        return;
      }

      await api.post('/api/admin/expenses/bulk-mark-paid', {
        expense_ids: expenseIds,
        user_name
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Refresh current page to reflect updated statuses
      fetchData(currentPage);

      onMessage(`${expenseIds.length} expenses marked as paid and report generated!`, 'success');

      // Optionally, clear status filter so user sees updated list
      // setStatusFilter('');
    } catch (error: any) {
      console.error('Bulk mark as paid failed:', error);
      const serverMsg = error?.response?.data?.message;
      onMessage(serverMsg || 'Failed to mark expenses as paid', 'error');
    } finally {
      setBulkMarkInProgress(false);
    }
  };

  const handleExpenseClick = (expense: Expense) => {
    try {
      const isDebug = (typeof window !== 'undefined') && (
        (localStorage.getItem('debugAdminExpenses') === '1') ||
        (new URLSearchParams(window.location.search).get('debug') === '1')
      );
      if (isDebug) console.log('[AdminExpenses] Selected expense for split view:', expense);
      
      setSelectedExpense(expense);
      
      // Handle receipt URL construction
      let receiptPath = '';
      if (expense.receipt) {
        // If receipt is already a full URL, use it directly
        if (expense.receipt.startsWith('http')) {
          receiptPath = expense.receipt;
        } 
        // If it's a path/filename, normalize it and ensure we include the uploads prefix
        else {
          const normalized = expense.receipt.startsWith('/')
            ? expense.receipt
            : `/${expense.receipt}`;

          receiptPath = normalized.startsWith('/uploads/')
            ? normalized
            : `/uploads${normalized}`;
        }
        
        // Use the fileUrl helper which handles environment-specific URL construction
        const fullUrl = fileUrl(receiptPath);
        if (isDebug) console.log('[AdminExpenses] Setting receipt URL:', fullUrl);
        setReceiptUrl(fullUrl);
      } else {
        setReceiptUrl('');
      }
      
      setShowSplitView(true);
      // Kick off duplicate fetch when opening the split view
      fetchDuplicates(expense);
    } catch (error) {
      console.error('Error in handleExpenseClick:', error);
      setReceiptUrl('');
    }
  };

  const getExpenseIdentifier = (e: Expense): string => {
    return (e as any).expense_id || String(e.unique_id || e.id);
  };

  // Small inline component to render a duplicates badge for a row
  const DupeCountBadge = ({ expense }: { expense: Expense }) => {
    // Only show badge for submitted/draft expenses
    if (expense.status !== 'submitted' && expense.status !== 'draft') return null;
    
    // Use the duplicate_count from the expense object (set by backend)
    const count = (expense as any).duplicate_count;
    
    if (!count || count <= 0) return null;
    return (
      <span
        title={count === 1 ? '1 possible duplicate' : `${count} possible duplicates`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '8px',
          padding: '0 8px',
          height: '20px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: 700 as any,
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          color: '#9a3412',
          border: '1px solid rgba(249, 115, 22, 0.45)',
          boxShadow: '0 3px 8px rgba(249, 115, 22, 0.25)',
          letterSpacing: 0.3,
          lineHeight: 1,
          textTransform: 'uppercase'
        }}
      >
        Dup {count}
      </span>
    );
  };

  const fetchDuplicates = async (expense: Expense) => {
    try {
      setDupeLoading(true);
      setDupeError('');
      setDupeList([]);
      const expIdentifier = encodeURIComponent(getExpenseIdentifier(expense));
      const resp = await api.get(`/api/admin/expenses/${expIdentifier}/duplicates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rows = (resp.data?.duplicates || []) as any[];
      // Normalize numbers similar to other fetches
      const normalized = rows.map((e: any) => ({
        ...e,
        amount: e.amount !== null && e.amount !== undefined ? Number(e.amount) : e.amount,
        reimbursement_amount: e.reimbursement_amount !== null && e.reimbursement_amount !== undefined ? Number(e.reimbursement_amount) : e.reimbursement_amount,
        exchange_rate: e.exchange_rate !== null && e.exchange_rate !== undefined ? Number(e.exchange_rate) : e.exchange_rate,
        vat_amount: e.vat_amount !== null && e.vat_amount !== undefined && e.vat_amount !== '' ? Number(e.vat_amount) : null
      }));
      setDupeList(normalized as Expense[]);
    } catch (err: any) {
      console.error('Failed to fetch duplicates:', err);
      setDupeError(err?.response?.data?.message || 'Failed to load duplicates');
    } finally {
      setDupeLoading(false);
    }
  };

  const closeSplitView = () => {
    setShowSplitView(false);
    setSelectedExpense(null);
    setReceiptUrl('');
  };

  const formatCurrency = (amount: number | null | undefined, currency: string = '') => {
    if (amount === null || amount === undefined) return 'N/A';
    // Format number with 2 decimal places and append 3-letter currency code
    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
  };

  const formatDate = (dateString: string) => {
    // DD/MM/YY
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  // Display first name + first letter of last name
  const shortName = (fullName: string) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const first = parts[0];
    const last = parts[parts.length - 1];
    const initial = last ? `${last[0].toUpperCase()}.` : '';
    return `${first} ${initial}`.trim();
  };

  // Backend already applies user/status/category filters.
  // Here we only apply free-text search (including amounts) on the loaded
  // result set.
  const filteredExpenses = expenses.filter(expense => {
    if (!searchTerm) return true;
    const query = searchTerm.toLowerCase().trim();

    // Remove all commas and spaces from query for numeric matching
    const cleanQuery = query.replace(/[, ]/g, '');
    const numericQuery = Number(cleanQuery.replace(',', '.'));

    // Normalize amount strings by removing separators
    const normalizeAmount = (val: any) => (val ?? '').toString().replace(/[, ]/g, '');

    const matchesNumeric = !isNaN(numericQuery) && (
      Number(expense.amount) === numericQuery ||
      Number(expense.reimbursement_amount) === numericQuery ||
      normalizeAmount(expense.amount) === cleanQuery ||
      normalizeAmount(expense.reimbursement_amount) === cleanQuery
    );

    // Text search across name, email, justification, ID and formatted amounts
    const formattedAmount = formatCurrency(expense.amount, expense.currency).toLowerCase();
    const formattedReim = formatCurrency(expense.reimbursement_amount, expense.reimbursement_currency).toLowerCase();
    const amountStr = normalizeAmount(expense.amount);
    const reimbursementStr = normalizeAmount(expense.reimbursement_amount);

    const matchesText =
      expense.user_name.toLowerCase().includes(query) ||
      expense.user_email.toLowerCase().includes(query) ||
      expense.justification.toLowerCase().includes(query) ||
      expense.type.toLowerCase().includes(query) ||
      (expense.date && expense.date.toLowerCase().includes(query)) ||
      (((expense as any).expense_id || '').toLowerCase().includes(query)) ||
      formattedAmount.includes(query) ||
      formattedReim.includes(query) ||
      amountStr.includes(query) ||
      reimbursementStr.includes(query);

    return matchesNumeric || matchesText;
  });

  // Sort filtered expenses by selected criterion (newest first)
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    const getTime = (e: Expense) => {
      if (sortBy === 'submission') {
        const d = e.created_at || e.date;
        return d ? new Date(d).getTime() : 0;
      }
      // sortBy === 'expense'
      return e.date ? new Date(e.date).getTime() : 0;
    };
    return getTime(b) - getTime(a);
  });

  // When searching, show all matching results; otherwise paginate.
  const paginatedExpenses = searchTerm
    ? sortedExpenses
    : sortedExpenses.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const totalPages = Math.ceil(totalExpenses / PAGE_SIZE);
  const lastPageIndex = Math.max(0, totalPages - 1);
  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < lastPageIndex;
  const isTruncated = backendTotalExpenses != null && backendTotalExpenses > FETCH_LIMIT;

  // Summary by status, totals in USD and EUR based on reimbursement currency
  type StatusSummary = {
    status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
    count: number;
    totalUSD: number;
    totalEUR: number;
  };

  // Order determines left-to-right placement in the summary grid
  // Put Draft/Rejected to the right as requested
  const statusOrder: StatusSummary['status'][] = ['submitted', 'approved', 'paid', 'draft', 'rejected'];

  const statusSummaries: StatusSummary[] = (() => {
    const map = new Map<StatusSummary['status'], { count: number; totalUSD: number; totalEUR: number }>();
    for (const s of statusOrder) map.set(s, { count: 0, totalUSD: 0, totalEUR: 0 });

    // If we have backend summary and no free-text search is applied, use backend counts.
    // Backend already applies status/user/category filters and includes ALL matching
    // expenses across pages, so this avoids under-counting when there are many pages.
    const useBackendCounts = !!backendSummary && !searchTerm;

    if (useBackendCounts) {
      for (const s of statusOrder) {
        const entry = map.get(s)!;
        entry.count = backendSummary[s] || 0;

        // Also hydrate totals from backend summary when available so that
        // amount totals always correspond to the same filtered set as counts.
        if (s === 'submitted') {
          entry.totalUSD = backendSummary.submitted_usd ?? 0;
          entry.totalEUR = backendSummary.submitted_eur ?? 0;
        } else if (s === 'approved') {
          entry.totalUSD = backendSummary.approved_usd ?? 0;
          entry.totalEUR = backendSummary.approved_eur ?? 0;
        } else if (s === 'rejected') {
          entry.totalUSD = backendSummary.rejected_usd ?? 0;
          entry.totalEUR = backendSummary.rejected_eur ?? 0;
        } else if (s === 'draft') {
          entry.totalUSD = backendSummary.draft_usd ?? 0;
          entry.totalEUR = backendSummary.draft_eur ?? 0;
        } else if (s === 'paid') {
          entry.totalUSD = backendSummary.paid_usd ?? 0;
          entry.totalEUR = backendSummary.paid_eur ?? 0;
        }
      }
    }

    // Always calculate amounts from filteredExpenses on the current page set.
    // (Backend does not yet expose amount totals per status.)
    for (const e of filteredExpenses) {
      const key = e.status as StatusSummary['status'];
      if (!map.has(key)) continue;
      const entry = map.get(key)!;

      // Only increment count from visible rows when we cannot rely on backendSummary,
      // e.g. when a free-text search is active.
      if (!useBackendCounts) {
        entry.count += 1;
      }

      // Only accumulate amounts client-side when we don't have backend totals
      if (!useBackendCounts) {
        const reimAmt = Number(e.reimbursement_amount) || 0;
        if (e.reimbursement_currency === 'USD') entry.totalUSD += reimAmt;
        if (e.reimbursement_currency === 'EUR') entry.totalEUR += reimAmt;
      }
    }

    return statusOrder.map((status) => ({ status, ...map.get(status)! }));
  })();

  const overallTotals = statusSummaries.reduce((acc, cur) => {
    acc.count += cur.count;
    acc.totalUSD += cur.totalUSD;
    acc.totalEUR += cur.totalEUR;
    return acc;
  }, { count: 0, totalUSD: 0, totalEUR: 0 });

  // Bulk mark-as-paid enablement and counts
  // Prefer backendSummary (which reflects ALL matching expenses across pages) when available
  // so the button count matches the summary card (e.g. 105 approved items), and fall back
  // to page-level counting only when we cannot rely on backendSummary (e.g. during search).
  const approvedCountForSelectedUser = (() => {
    if (!userFilter || statusFilter !== 'approved') return 0;

    // When backendSummary is present and no free-text search is applied, it already
    // includes the full approved count for the selected user + filters across pages.
    if (backendSummary && !searchTerm) {
      return backendSummary.approved || 0;
    }

    // Fallback: count approved rows for the selected user from the current expenses page.
    return expenses.filter(e => e.user_email === userFilter && e.status === 'approved').length;
  })();

  const canBulkMarkAsPaid = Boolean(userFilter) && statusFilter === 'approved' && approvedCountForSelectedUser > 0;

  const getStatusBadge = (status: string) => {
    const statusColors = {
      draft: { bg: '#f3f4f6', text: '#374151' },
      submitted: { bg: '#fef3c7', text: '#92400e' },
      approved: { bg: '#d1fae5', text: '#065f46' },
      rejected: { bg: '#fee2e2', text: '#991b1b' },
      paid: { bg: '#dbeafe', text: '#1e40af' }
    };
    const colors = statusColors[status as keyof typeof statusColors] || statusColors.draft;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: colors.bg,
        color: colors.text,
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading expenses...
      </div>
    );
  }

  return (
    <div className="admin-expenses">
      <div className="admin-expenses__summary">
        <div className="admin-expenses__summary-header">
          <h3 className="admin-expenses__summary-title">Expense Summary (by Status)</h3>
          <div className="admin-expenses__summary-meta">
            <div>Totals reflect current filters and search</div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="admin-expenses__create-btn"
              title="Create expense on behalf of a user"
            >
              + Create Expense
            </button>
          </div>
        </div>
        <div className="admin-expenses__summary-grid">
          {statusSummaries.map((s) => (
            <div key={s.status} className="admin-expenses__summary-card">
              <div className="admin-expenses__summary-card-row">
                <div className="admin-expenses__summary-card-title">{s.status}</div>
                <div className="admin-expenses__summary-card-count">{s.count} item{s.count !== 1 ? 's' : ''}</div>
              </div>
              <div className="admin-expenses__summary-card-metrics">
                <div className="admin-expenses__summary-metric">
                  <span>Total USD</span>
                  <span>{formatCurrency(s.totalUSD, 'USD')}</span>
                </div>
                <div className="admin-expenses__summary-metric">
                  <span>Total EUR</span>
                  <span>{formatCurrency(s.totalEUR, 'EUR')}</span>
                </div>
              </div>
              {s.status === 'approved' && (
                <div className="admin-expenses__summary-card-action">
                  <button
                    onClick={handleBulkMarkAsPaid}
                    disabled={!canBulkMarkAsPaid || bulkMarkInProgress}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: (!canBulkMarkAsPaid || bulkMarkInProgress) ? '#9ca3af' : '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600 as any,
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      lineHeight: 1.2,
                      cursor: (!canBulkMarkAsPaid || bulkMarkInProgress) ? 'not-allowed' : 'pointer'
                    }}
                    title={!userFilter ? 'Select a user to enable' : (statusFilter !== 'approved' ? 'Set status filter to Approved' : '')}
                  >
                    {bulkMarkInProgress ? 'Processing…' : `Mark all approved as paid (${approvedCountForSelectedUser})`}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="admin-expenses__filters">
          <div className="admin-expenses__filters-row">
            <div className="admin-expenses__filters-item">
              <label style={{ display: 'none' }}>User</label>
              <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="admin-expenses__control">
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.email}>{user.first_name} {user.last_name}</option>
              ))}
              </select>
            </div>
            <div className="admin-expenses__filters-item">
              <label style={{ display: 'none' }}>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-expenses__control">
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
              </select>
            </div>
            <div className="admin-expenses__filters-item">
              <label style={{ display: 'none' }}>Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="admin-expenses__control">
              <option value="">All Categories</option>
              {ALL_CATEGORY_VALUES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              </select>
            </div>
            <div className="admin-expenses__filters-item">
              <label style={{ display: 'none' }}>Sort By</label>
              <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'submission' | 'expense')}
              className="admin-expenses__control"
            >
              <option value="submission">Submission (newest)</option>
              <option value="expense">Expense (newest)</option>
            </select>
            </div>
            <div className="admin-expenses__filters-item admin-expenses__filters-item--search">
              <label style={{ display: 'none' }}>Search</label>
              <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search expenses..."
              className="admin-expenses__control"
            />
            </div>
          </div>
        </div>
        {isTruncated && (
          <div
            style={{
              marginTop: '8px',
              marginBottom: '8px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              border: '1px solid #f59e0b',
              fontSize: '13px'
            }}
          >
            {`Showing only the first ${FETCH_LIMIT.toLocaleString()} expenses matching your filters out of ${backendTotalExpenses!.toLocaleString()}. Sorting and search apply only to the loaded subset.`}
          </div>
        )}
      </div>
      <div className="admin-expenses__cards">
        {paginatedExpenses.map((expense) => (
          <article
            key={expense.id}
            className="admin-expenses-card"
            onClick={() => handleExpenseClick(expense)}
          >
            <header className="admin-expenses-card__header">
              <div className="admin-expenses-card__meta">
                <div className="admin-expenses-card__date">{formatDate(expense.date)}</div>
                <div className="admin-expenses-card__type">{expense.type}</div>
                <div className="admin-expenses-card__user">{expense.user_name}</div>
              </div>
              <div className="admin-expenses-card__status-column">
                <div className="admin-expenses-card__status">{getStatusBadge(expense.status)}</div>
                <div className="admin-expenses-card__dupes">
                  <DupeCountBadge expense={expense} />
                </div>
              </div>
            </header>

            <div className="admin-expenses-card__grid">
              <div className="admin-expenses-card__details">
                <div className="admin-expenses-card__field">
                  <span className="admin-expenses-card__label">Amount</span>
                  <span className="admin-expenses-card__value">{formatCurrency(expense.amount, expense.currency)}</span>
                </div>
                <div className="admin-expenses-card__field">
                  <span className="admin-expenses-card__label">Reimbursed</span>
                  <span className="admin-expenses-card__value">{formatCurrency(expense.reimbursement_amount, expense.reimbursement_currency)}</span>
                </div>
                {(typeof expense.vat_amount === 'number' && expense.vat_amount > 0) && (
                  <div className="admin-expenses-card__field">
                    <span className="admin-expenses-card__label">VAT</span>
                    <span className="admin-expenses-card__value admin-expenses-card__value--muted">{formatCurrency(expense.vat_amount, expense.currency)}</span>
                  </div>
                )}
                <div className="admin-expenses-card__field">
                  <span className="admin-expenses-card__label">Rate</span>
                  <span className="admin-expenses-card__value admin-expenses-card__value--muted">
                    {(expense.override_exchange_rate ?? expense.exchange_rate) != null 
                      ? `${(expense.override_exchange_rate ?? expense.exchange_rate).toFixed(4)} ${expense.currency}/${expense.reimbursement_currency}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="admin-expenses-card__field">
                  <span className="admin-expenses-card__label">Expense ID</span>
                  <span className="admin-expenses-card__value admin-expenses-card__value--code">{(expense as any).expense_id || '—'}</span>
                </div>
              </div>

              <div className="admin-expenses-card__notes">
                <span className="admin-expenses-card__label">Justification</span>
                <p className="admin-expenses-card__justification-text" title={expense.justification}>
                  {expense.justification || 'No justification provided.'}
                </p>
              </div>
            </div>

            <footer className="admin-expenses-card__actions" onClick={(e) => e.stopPropagation()}>
              <div className="admin-expenses-card__action-group">
                <button
                  className="admin-expenses-card__action admin-expenses-card__action--secondary"
                  onClick={() => handleExpenseClick(expense)}
                >
                  View details
                </button>
                {expense.status === 'submitted' && (
                  <>
                    <button
                      className="admin-expenses-card__action admin-expenses-card__action--success"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleExpenseAction(expense.id, 'approve');
                      }}
                    >
                      Approve
                    </button>
                    <button
                      className="admin-expenses-card__action admin-expenses-card__action--danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRejectClick(expense);
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
              <button
                className={`admin-expenses-card__action admin-expenses-card__action--primary${expense.status === 'paid' ? ' is-disabled' : ''}`}
                disabled={expense.status === 'paid'}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditingExpense(expense);
                  setShowAdminEdit(true);
                }}
              >
                Admin edit
              </button>
            </footer>
          </article>
        ))}

        {sortedExpenses.length === 0 && (
          <div className="admin-expenses-card__empty">No expenses found matching your filters.</div>
        )}
      </div>

      <div className="admin-expenses__table-wrapper">
        <div className="admin-expenses__table-scroll">
          <table className="admin-expenses__table">
            <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>User</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Date</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Category</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Reimb.</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Rate</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Justification</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>ID</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedExpenses.map((expense, index) => {
                const baseBg = index % 2 === 0 ? '#ffffff' : '#fcfcfd';
                return (
                <tr
                  key={expense.id}
                  onClick={() => handleExpenseClick(expense)}
                  style={{ borderBottom: index < paginatedExpenses.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', backgroundColor: baseBg }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = baseBg; }}
                >
                  <td style={{ padding: '8px 10px' }}>{shortName(expense.user_name)}</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', color: '#1f2937', whiteSpace: 'nowrap' }}>{formatDate(expense.date)}</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', color: '#1f2937', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{expense.type}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
                        {formatCurrency(expense.amount, expense.currency)}
                      </div>
                      {(typeof expense.vat_amount === 'number' && expense.vat_amount > 0) && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          <span style={{ fontWeight: 600 }}>VAT:</span> {formatCurrency(expense.vat_amount, expense.currency)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}
                    title={`Original: ${formatCurrency(expense.amount, expense.currency)}\nReimbursement: ${formatCurrency(expense.reimbursement_amount, expense.reimbursement_currency)}`}
                  >
                    {formatCurrency(expense.reimbursement_amount, expense.reimbursement_currency)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <span
                          style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            color: '#92400e',
                            backgroundColor: '#fef3c7',
                            padding: '1px 4px',
                            borderRadius: '9999px',
                            letterSpacing: 0,
                            visibility: expense.override_exchange_rate ? 'visible' as any : 'hidden' as any
                          }}
                          title={expense.override_exchange_rate ? 'Rate overridden' : ''}
                        >
                          OVR
                        </span>
                        <span style={{ fontSize: '13px', color: expense.override_exchange_rate ? '#b45309' : '#1f2937', fontWeight: 600 }}>
                          {(expense.override_exchange_rate ?? expense.exchange_rate) != null 
                            ? (expense.override_exchange_rate ?? expense.exchange_rate).toFixed(4)
                            : 'N/A'}
                        </span>
                      </span>
                      <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>{expense.currency}/{expense.reimbursement_currency}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', color: '#1f2937', maxWidth: '220px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={expense.justification}>{expense.justification}</div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{getStatusBadge(expense.status)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#1f2937', fontWeight: 600 as any }}>
                      {(expense as any).expense_id || '—'}
                    </div>
                    {/* Duplicates count badge visible directly in the main list */}
                    <div style={{ marginTop: '4px' }}>
                      <DupeCountBadge expense={expense} />
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                      {expense.status === 'submitted' && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); handleExpenseAction(expense.id, 'approve'); }} style={{ padding: '6px 10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', width: '100%' }}>Approve</button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setRejectingExpense(expense); setRejectionReason(''); setShowRejectModal(true); }}
                            style={{ padding: '6px 10px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', width: '100%' }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingExpense(expense); setShowAdminEdit(true); }}
                        disabled={expense.status === 'paid'}
                        style={{ padding: '6px 10px', backgroundColor: expense.status === 'paid' ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: expense.status === 'paid' ? 'not-allowed' : 'pointer', width: '100%' }}
                        title={expense.status === 'paid' ? 'Paid expenses cannot be edited' : 'Admin edit expense'}
                      >
                        Admin Edit
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sortedExpenses.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280', fontSize: '16px' }}>No expenses found matching your filters.</div>
        )}
      </div>

      {/* Pagination Controls */}
      {!searchTerm && totalExpenses > PAGE_SIZE && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Showing {currentPage * PAGE_SIZE + 1}-{Math.min((currentPage + 1) * PAGE_SIZE, totalExpenses)} of {totalExpenses} expenses
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                if (!canGoPrev) return;
                setCurrentPage(prev => Math.max(0, prev - 1));
              }}
              disabled={!canGoPrev}
              style={{
                padding: '8px 16px',
                backgroundColor: !canGoPrev ? '#e5e7eb' : '#3b82f6',
                color: !canGoPrev ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: !canGoPrev ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Previous
            </button>
            <button
              onClick={() => {
                if (!canGoNext) return;
                setCurrentPage(prev => Math.min(lastPageIndex, prev + 1));
              }}
              disabled={!canGoNext}
              style={{
                padding: '8px 16px',
                backgroundColor: !canGoNext ? '#e5e7eb' : '#3b82f6',
                color: !canGoNext ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: !canGoNext ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Split View Modal */}
      {showSplitView && selectedExpense && (
        <div className="admin-expenses__modal-overlay">
          <div className="admin-expenses__modal">
            <header className="admin-expenses__modal-header">
              <div>
                <h2 className="admin-expenses__modal-title">Expense Details & Receipt</h2>
                <p className="admin-expenses__modal-subtitle">Review the submission and take action.</p>
              </div>
              <button className="admin-expenses__modal-close" onClick={closeSplitView} aria-label="Close">✕</button>
            </header>

            <div className="admin-expenses__modal-body">
              <div className="admin-expenses__modal-left">
                <section className="admin-expenses__modal-card">
                  <header className="admin-expenses__modal-card-header">
                    <div>
                      <h3 className="admin-expenses__modal-card-date">{formatDate(selectedExpense.date)}</h3>
                      <p className="admin-expenses__modal-card-type">{selectedExpense.type}</p>
                    </div>
                    <div className="admin-expenses__modal-status">{getStatusBadge(selectedExpense.status)}</div>
                  </header>
                  <div className="admin-expenses__modal-grid admin-expenses__modal-grid--stack">
                    <div>
                      <span className="admin-expenses__modal-label">User</span>
                      <p className="admin-expenses__modal-text">{selectedExpense.user_name}</p>
                      <p className="admin-expenses__modal-text admin-expenses__modal-text--muted">{selectedExpense.user_email}</p>
                    </div>
                    <div>
                      <span className="admin-expenses__modal-label">Expense ID</span>
                      <p className="admin-expenses__modal-text admin-expenses__modal-text--code">{(selectedExpense as any).expense_id || '—'}</p>
                    </div>
                    <div>
                      <span className="admin-expenses__modal-label">Classification</span>
                      <p className="admin-expenses__modal-text">{selectedExpense.classification || 'Not provided'}</p>
                    </div>
                    <div>
                      <span className="admin-expenses__modal-label">Approved by</span>
                      <p className="admin-expenses__modal-text admin-expenses__modal-text--muted">{selectedExpense.approved_by_email || '—'}</p>
                    </div>
                  </div>
                </section>

                <section className="admin-expenses__modal-card">
                  <header className="admin-expenses__modal-card-header">
                    <h3 className="admin-expenses__modal-card-date">Amounts</h3>
                    <div className="admin-expenses__modal-status">
                      {dupeLoading ? (
                        <span className="admin-expenses__modal-text admin-expenses__modal-text--muted">Checking…</span>
                      ) : dupeError ? (
                        <span className="admin-expenses__modal-text admin-expenses__modal-text--error">{dupeError}</span>
                      ) : (
                        <span className={`admin-expenses__modal-dupes${dupeList.length ? ' has-dupes' : ''}`}>
                          {dupeList.length} potential dupes
                        </span>
                      )}
                    </div>
                  </header>
                  <div className="admin-expenses__modal-grid">
                    <div>
                      <span className="admin-expenses__modal-label">Amount</span>
                      <span className="admin-expenses__modal-value">{formatCurrency(selectedExpense.amount, selectedExpense.currency)}</span>
                    </div>
                    <div>
                      <span className="admin-expenses__modal-label">Reimbursed</span>
                      <span className="admin-expenses__modal-value">{formatCurrency(selectedExpense.reimbursement_amount, selectedExpense.reimbursement_currency)}</span>
                    </div>
                    {(typeof selectedExpense.vat_amount === 'number' && selectedExpense.vat_amount > 0) && (
                      <div>
                        <span className="admin-expenses__modal-label">VAT</span>
                        <span className="admin-expenses__modal-text">{formatCurrency(selectedExpense.vat_amount, selectedExpense.currency)}</span>
                      </div>
                    )}
                    <div>
                      <span className="admin-expenses__modal-label">Exchange rate</span>
                      <span className="admin-expenses__modal-text">
                        {(selectedExpense.override_exchange_rate ?? selectedExpense.exchange_rate) != null
                          ? `${(selectedExpense.override_exchange_rate ?? selectedExpense.exchange_rate).toFixed(4)} ${selectedExpense.currency}/${selectedExpense.reimbursement_currency}`
                          : 'N/A'}
                        {selectedExpense.override_exchange_rate && (
                          <span style={{ 
                            marginLeft: '8px', 
                            padding: '2px 6px', 
                            backgroundColor: '#fef3c7', 
                            color: '#92400e', 
                            borderRadius: '4px', 
                            fontSize: '11px',
                            fontWeight: 600
                          }}>
                            OVERRIDDEN
                          </span>
                        )}
                      </span>
                    </div>
                    {selectedExpense.override_exchange_rate && selectedExpense.override_reason && (
                      <div>
                        <span className="admin-expenses__modal-label">Override reason</span>
                        <p className="admin-expenses__modal-text" style={{ fontStyle: 'italic', color: '#92400e' }}>
                          {selectedExpense.override_reason}
                        </p>
                      </div>
                    )}
                    {selectedExpense.override_exchange_rate && selectedExpense.fetched_exchange_rate && (
                      <div>
                        <span className="admin-expenses__modal-label">Original fetched rate</span>
                        <span className="admin-expenses__modal-text" style={{ color: '#6b7280' }}>
                          {selectedExpense.fetched_exchange_rate.toFixed(4)} {selectedExpense.currency}/{selectedExpense.reimbursement_currency}
                          {(() => {
                            const rawVariation = ((selectedExpense.override_exchange_rate - selectedExpense.fetched_exchange_rate) / selectedExpense.fetched_exchange_rate) * 100;
                            let variation = rawVariation;
                            const absVariation = Math.abs(variation);

                            // Treat very small differences as zero so using the fetched rate shows 0.00%
                            if (absVariation < 0.005) {
                              variation = 0;
                            }

                            let bg = '#e5e7eb';
                            let fg = '#374151';
                            let sign = '';

                            if (variation < 0) {
                              // Override lower than fetched -> negative deviation (green)
                              bg = '#dcfce7';
                              fg = '#166534';
                              sign = '-';
                            } else if (variation > 0) {
                              if (absVariation > 5) {
                                // Above 5% limit -> red
                                bg = '#fee2e2';
                                fg = '#991b1b';
                                sign = '+';
                              } else {
                                // Positive but within 5% -> orange
                                bg = '#ffedd5';
                                fg = '#c2410c';
                                sign = '+';
                              }
                            }

                            const displayValue = Math.abs(variation).toFixed(2);

                            return (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  padding: '2px 6px',
                                  backgroundColor: bg,
                                  color: fg,
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}
                              >
                                {variation === 0 ? '0.00%' : `${sign}${displayValue}%`}
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="admin-expenses__modal-label">Justification</span>
                    <p className="admin-expenses__modal-text">{selectedExpense.justification || 'No justification provided.'}</p>
                  </div>

                  {dupeList.length > 0 && !dupeError && !dupeLoading && (
                    <div className="admin-expenses__modal-duplicates">
                      <span className="admin-expenses__modal-label">Possible duplicates</span>
                      <div className="admin-expenses__modal-duplicates-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Description</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>ID</th>
                              <th>Receipt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dupeList.map((dupe, index) => (
                              <tr key={index}>
                                <td>{formatDate(dupe.date)}</td>
                                <td title={dupe.justification}>{dupe.justification}</td>
                                <td>{formatCurrency(dupe.amount, dupe.currency)}</td>
                                <td>
                                  <span className={`admin-expenses__modal-dupe-status admin-expenses__modal-dupe-status--${dupe.status}`}>
                                    {dupe.status}
                                  </span>
                                </td>
                                <td>{dupe.expense_id || dupe.unique_id || dupe.id}</td>
                                <td>
                                  {dupe.receipt ? (
                                    <a href={fileUrl(`/uploads/${dupe.receipt}`)} target="_blank" rel="noopener noreferrer">View</a>
                                  ) : (
                                    <span className="admin-expenses__modal-text admin-expenses__modal-text--muted">None</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>

                {selectedExpense.status !== 'paid' && (
                  <section className="admin-expenses__modal-card admin-expenses__modal-card--actions">
                    <span className="admin-expenses__modal-label">Quick actions</span>
                    <div className="admin-expenses__modal-actions">
                      {selectedExpense.status !== 'submitted' && (
                        <button
                          className="admin-expenses__modal-action admin-expenses__modal-action--neutral"
                          onClick={() => { handleExpenseAction(selectedExpense.id, 'revert'); closeSplitView(); }}
                        >
                          Mark as Submitted
                        </button>
                      )}
                      {selectedExpense.status !== 'approved' && (
                        <button
                          className="admin-expenses__modal-action admin-expenses__modal-action--approve"
                          onClick={() => { handleExpenseAction(selectedExpense.id, 'approve'); closeSplitView(); }}
                        >
                          Approve
                        </button>
                      )}
                      {selectedExpense.status !== 'rejected' && (
                        <button
                          className="admin-expenses__modal-action admin-expenses__modal-action--reject"
                          onClick={() => {
                            handleRejectClick(selectedExpense);
                            closeSplitView();
                          }}
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  </section>
                )}
                
                {/* Audit Trail Section */}
                <section className="admin-expenses__modal-card">
                  <AuditTrail 
                    expenseId={(() => {
                      const id = selectedExpense.expense_id || selectedExpense.unique_id;
                      console.log('[AdminExpenses] Passing expense_id to AuditTrail:', id, 'from expense:', selectedExpense);
                      return id;
                    })()} 
                    token={localStorage.getItem('token') || ''} 
                  />
                </section>
              </div>

              <div className="admin-expenses__modal-right">
                <header className="admin-expenses__modal-receipt-header">
                  <h3 className="admin-expenses__modal-card-date">Receipt</h3>
                  {receiptUrl && (
                    <a className="admin-expenses__modal-receipt-link" href={receiptUrl} target="_blank" rel="noopener noreferrer">
                      Open in new tab ↗
                    </a>
                  )}
                </header>
                <div className="admin-expenses__modal-receipt">
                  {receiptUrl ? (
                    receiptUrl.toLowerCase().includes('.pdf') ? (
                      <iframe src={receiptUrl} title="Receipt PDF" />
                    ) : (
                      <img src={receiptUrl} alt="Receipt" />
                    )
                  ) : (
                    <p className="admin-expenses__modal-empty">No receipt available</p>
                  )}
                </div>
                {receiptUrl && (
                  <div className="admin-expenses__modal-receipt-actions">
                    <a href={receiptUrl} target="_blank" rel="noopener noreferrer">Open in new tab</a>
                    <a href={receiptUrl} download>Download</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Reject Expense
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Please provide a reason for rejecting this expense. The user will see this message.
              </p>
            </div>
            
            {rejectingExpense && (
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                marginBottom: '16px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  {rejectingExpense.type} - {rejectingExpense.currency} {rejectingExpense.amount}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {new Date(rejectingExpense.date).toLocaleDateString()} • {rejectingExpense.justification}
                </div>
              </div>
            )}
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please explain why this expense is being rejected..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleRejectCancel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectionReason.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: rejectionReason.trim() ? '#ef4444' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: rejectionReason.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Reject Expense
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Edit Modal */}
      {showAdminEdit && editingExpense && (
        <AdminExpenseEditModal
          isOpen={showAdminEdit}
          expense={editingExpense as any}
          onClose={() => setShowAdminEdit(false)}
          onSaved={async () => {
            const editedExpenseDbId = editingExpense?.id;
            
            // Close the modal
            setShowAdminEdit(false);
            setEditingExpense(null);
            
            // Fetch updated expense data (backend will recalculate duplicate_count)
            await fetchData(currentPage);
            
            // If split view is open for the edited expense, refresh the duplicate list
            if (editedExpenseDbId && showSplitView && selectedExpense && selectedExpense.id === editedExpenseDbId) {
              const updatedExpense = expenses.find(e => e.id === editedExpenseDbId);
              if (updatedExpense) {
                setSelectedExpense(updatedExpense);
                await fetchDuplicates(updatedExpense);
              }
            }
          }}
        />
      )}

      {/* Admin Create Modal */}
      {showCreateModal && (
        <AdminExpenseCreateModal
          isOpen={showCreateModal}
          users={users as any}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            // Fetch data - backend will calculate duplicate_count
            fetchData(currentPage);
          }}
        />
      )}

      {/* Duplicate Warning Modal for Approval */}
      {showDupeApproveModal && approvingExpense && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>
                ⚠️ Potential Duplicate Detected
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                {approveDuplicates.length} {approveDuplicates.length === 1 ? 'expense' : 'expenses'} with the same date, amount, and currency {approveDuplicates.length === 1 ? 'has' : 'have'} already been approved or paid for this user.
              </p>
            </div>
            
            {/* Expense being approved */}
            <div style={{
              padding: '16px',
              backgroundColor: '#fef3c7',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '2px solid #f59e0b'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#92400e', marginBottom: '8px', textTransform: 'uppercase' }}>
                Expense to Approve
              </div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                {approvingExpense.type} - {formatCurrency(approvingExpense.amount, approvingExpense.currency)}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {formatDate(approvingExpense.date)} • {approvingExpense.justification}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                ID: {(approvingExpense as any).expense_id || approvingExpense.id}
              </div>
            </div>

            {/* Duplicate expenses list */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Existing {approveDuplicates.length === 1 ? 'Duplicate' : 'Duplicates'}:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {approveDuplicates.map((dupe, idx) => (
                  <div key={dupe.id} style={{
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #fca5a5'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#991b1b', marginBottom: '4px' }}>
                      {dupe.type} - {formatCurrency(dupe.amount, dupe.currency)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#7f1d1d' }}>
                      {formatDate(dupe.date)} • {dupe.justification}
                    </div>
                    <div style={{ fontSize: '11px', color: '#7f1d1d', marginTop: '4px', display: 'flex', gap: '12px' }}>
                      <span>ID: {(dupe as any).expense_id || dupe.id}</span>
                      <span>Status: <strong>{dupe.status}</strong></span>
                      {dupe.approved_at && <span>Approved: {new Date(dupe.approved_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid #fbbf24'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                <strong>⚠️ Warning:</strong> Approving this expense may result in duplicate reimbursement. Please verify that this is a legitimate separate expense before proceeding.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleDupeApproveCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDupeApproveConfirm}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Approve Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpensesTab;

